import cn from 'classnames';
import { useEffect, useState } from 'react';
import { trackError } from '#api/errors.ts';
import { CopyIcon } from '#assets/ReactSvg.ts';
import { Tooltip } from '#components/atoms/tooltip/Tooltip.tsx';
import { useTimeout } from '#hooks/useTimeout.ts';
import { copyToClipboard } from '#util/copy-to-clipboard.ts';
import styles from './EnrollmentCodeCopyButton.module.css';

type Props = {
  token: string;
};

export function EnrollmentCodeCopyButton({ token }: Props) {
  const initialTooltip = 'Copy enrollment code';
  const successTooltip = 'Copied!';

  const [tooltip, setTooltip] = useState(initialTooltip);
  const { start: resetTooltipAfterDelay, clear: clearTimeout } = useTimeout(() => {
    setTooltip(initialTooltip);
  }, 2000);

  // Reset the tooltip after 2 seconds
  useEffect(() => {
    if (tooltip === successTooltip) {
      resetTooltipAfterDelay();
    }
  }, [resetTooltipAfterDelay, successTooltip, tooltip]);

  if (!token) return null;

  return (
    <Tooltip
      content={tooltip}
      className={cn(styles.EnrollmentCodeCopyButton)}
      onHidden={() => {
        setTooltip(initialTooltip);
        clearTimeout();
      }}
      timeout={0}
      placement="right"
      flip="top"
      render={
        <button
          type="button"
          onClick={() => {
            copyToClipboard(token)
              .then(() => {
                return setTooltip(successTooltip);
              })
              .catch((err: unknown) => {
                console.error('Could not copy');
                setTooltip('Unable to copy');
                trackError(err);
              });
          }}
        />
      }
    >
      <span className={styles.EnrollmentCodeCopyButton_label}>
        <span className="a">Copy enrollment code</span>
      </span>
      <span className={styles.EnrollmentCodeCopyButton_tokenContainer}>
        <span className={cn('a truncate', styles.EnrollmentCodeCopyButton_token)}>
          {`${token.slice(0, 8)}...${token.slice(-8)}`}
        </span>
      </span>
      <CopyIcon className={'color-lowlight'} aria-hidden="true" />
    </Tooltip>
  );
}
